import React, { useState, useEffect } from "react";
import { useGetImagesQuery } from "state/api";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

const ImagePreviewModal = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentProduct, setCurrentProduct] = useState("");
  const [products, setProducts] = useState([]);

  const {
    data: response,
    isLoading,
    error,
  } = useGetImagesQuery({
    page: 1,
    limit: 100,
    month: currentMonth,
    year: currentYear,
    product: currentProduct || null,
    day: null,
  });

  // Safely extract data as array
  const appdata = Array.isArray(response?.data) ? response?.data : [];

  // Extract unique products
  useEffect(() => {
    if (appdata.length > 0) {
      const uniqueProducts = [
        ...new Set(appdata.map((img) => img.product).filter(Boolean)),
      ];
      setProducts(uniqueProducts.sort());
    }
  }, [appdata]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#0f172a",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: 4, background: "#0f172a", minHeight: "100vh" }}>
        <Alert severity="error">
          {error?.data?.message || "Failed to load images"}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: 4,
        background: "#0f172a",
        minHeight: "100vh",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          color: "white",
          fontWeight: 700,
          marginBottom: 4,
        }}
      >
        Prescription Images Preview
      </Typography>

      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          marginBottom: 4,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <FormControl
          sx={{
            minWidth: 150,
            "& .MuiOutlinedInput-root": {
              color: "white",
              backgroundColor: "rgba(30, 41, 59, 0.8)",
              "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
              "&:hover fieldset": { borderColor: "#3b82f6" },
              "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
            },
            "& .MuiInputBase-input": { color: "white" },
            "& .MuiSvgIcon-root": { color: "white" },
          }}
        >
          <InputLabel sx={{ color: "white" }}>Month</InputLabel>
          <Select
            value={currentMonth}
            onChange={(e) => setCurrentMonth(e.target.value)}
            label="Month"
            sx={{
              "& .MuiMenuItem-root": { color: "white" },
              "& .MuiMenu-paper": { backgroundColor: "#1e293b" },
            }}
          >
            {Array.from({ length: 12 }, (_, i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {new Date(2024, i).toLocaleString("default", {
                  month: "long",
                })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          sx={{
            minWidth: 150,
            "& .MuiOutlinedInput-root": {
              color: "white",
              backgroundColor: "rgba(30, 41, 59, 0.8)",
              "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
              "&:hover fieldset": { borderColor: "#3b82f6" },
              "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
            },
            "& .MuiInputBase-input": { color: "white" },
            "& .MuiSvgIcon-root": { color: "white" },
          }}
        >
          <InputLabel sx={{ color: "white" }}>Year</InputLabel>
          <Select
            value={currentYear}
            onChange={(e) => setCurrentYear(e.target.value)}
            label="Year"
            sx={{
              "& .MuiMenuItem-root": { color: "white" },
              "& .MuiMenu-paper": { backgroundColor: "#1e293b" },
            }}
          >
            {Array.from({ length: 5 }, (_, i) => {
              const yearOption = new Date().getFullYear() - i;
              return (
                <MenuItem key={yearOption} value={yearOption}>
                  {yearOption}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <FormControl
          sx={{
            minWidth: 180,
            "& .MuiOutlinedInput-root": {
              color: "white",
              backgroundColor: "rgba(30, 41, 59, 0.8)",
              "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
              "&:hover fieldset": { borderColor: "#3b82f6" },
              "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
            },
            "& .MuiInputBase-input": { color: "white" },
            "& .MuiSvgIcon-root": { color: "white" },
          }}
        >
          <InputLabel sx={{ color: "white" }}>Product</InputLabel>
          <Select
            value={currentProduct}
            onChange={(e) => setCurrentProduct(e.target.value)}
            label="Product"
            sx={{
              "& .MuiMenuItem-root": { color: "white" },
              "& .MuiMenu-paper": { backgroundColor: "#1e293b" },
            }}
          >
            <MenuItem value="">All Products</MenuItem>
            {products.map((product) => (
              <MenuItem key={product} value={product}>
                {product}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {appdata.length === 0 ? (
        <Typography sx={{ color: "#94a3b8" }}>No images found</Typography>
      ) : (
        <Grid container spacing={3}>
          {appdata.map((image) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
              <Card
                sx={{
                  background: "#1e293b",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 24px rgba(59, 130, 246, 0.3)",
                  },
                }}
              >
                {image.pic ? (
                  <CardMedia
                    component="img"
                    height="250"
                    image={image.pic}
                    alt={image.NAME || "Prescription"}
                    sx={{ objectFit: "cover" }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 250,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(30, 41, 59, 0.8)",
                      color: "#94a3b8",
                    }}
                  >
                    No Image
                  </Box>
                )}
                <CardContent sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#f1f5f9",
                      fontWeight: 600,
                      marginBottom: 1,
                    }}
                  >
                    {image.NAME}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#94a3b8",
                      display: "block",
                      marginBottom: 0.5,
                    }}
                  >
                    Doctor: {image.DoctName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#94a3b8",
                      display: "block",
                      marginBottom: 0.5,
                    }}
                  >
                    Category: {image.DocCategory}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#94a3b8",
                      display: "block",
                      marginBottom: 0.5,
                    }}
                  >
                    Product: {image.product}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#94a3b8", display: "block", marginBottom: 1 }}
                  >
                    MCL Code: {image.MclCode}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        image.STATUS === "APPROVED"
                          ? "#10b981"
                          : image.STATUS === "REJECTED"
                          ? "#ef4444"
                          : "#f59e0b",
                      display: "block",
                      fontWeight: 600,
                    }}
                  >
                    Status: {image.STATUS}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ImagePreviewModal;
